import type { SetupTourDefinition } from '../core/types';

export const CALENDAR_SETUP_TOUR_TARGETS = {
  overview: 'calendar-tour-overview',
  controls: 'calendar-tour-controls',
  schedule: 'calendar-tour-schedule',
  emptyFleetReady: 'calendar-tour-empty-fleet-ready',
  legend: 'calendar-tour-legend',
  groups: 'calendar-tour-groups',
  expandGroups: 'calendar-tour-expand-groups',
  models: 'calendar-tour-models',
  vehicles: 'calendar-tour-vehicles',
  addBlock: 'calendar-tour-add-block',
  blockForm: 'calendar-tour-block-form',
  blockModalFields: 'calendar-tour-block-modal-fields',
} as const;

/** Asks the Calendar page to clear its vehicle type and station filters, keeping the dates. */
export const CALENDAR_TOUR_PREPARE_FLEET_EVENT = 'calendar-tour:prepare-fleet';

/** Asks the Calendar page to close the Create Block form the tour opened. */
export const CALENDAR_TOUR_CLOSE_BLOCK_MODAL_EVENT = 'calendar-tour:close-block-modal';

export const calendarSetupTour: SetupTourDefinition = {
  id: 'calendar_tour',
  label: 'Calendar Tour',
  onboardingKey: 'calendar_tour',
  requiresRentalContext: true,
  steps: [
    {
      id: 'overview',
      route: '/calendar',
      target: CALENDAR_SETUP_TOUR_TARGETS.overview,
      content:
        'The Calendar brings vehicles, reservations, and availability together across the selected date range.',
    },
    {
      id: 'controls',
      route: '/calendar',
      target: CALENDAR_SETUP_TOUR_TARGETS.controls,
      content:
        'Vehicle type and station filters narrow the schedule, while the day controls and arrows change the visible period.',
      // Below the toolbar but against its right edge, so the filter dropdowns on the left
      // open beside the popover instead of underneath it.
      position: ({ right, bottom, width }) => [Math.max(right - width - 10, 0), bottom + 10],
    },
    {
      id: 'schedule',
      route: '/calendar',
      target: CALENDAR_SETUP_TOUR_TARGETS.schedule,
      content:
        'The schedule aligns each vehicle with its reservations and blocks across the visible dates.',
    },
    {
      id: 'legend',
      route: '/calendar',
      target: CALENDAR_SETUP_TOUR_TARGETS.legend,
      content:
        'The legend identifies reservation states and other blocked periods shown in the schedule. Next clears the vehicle type and station filters for the fleet walkthrough.',
      next: {
        // Branch only on the settled answer to the unfiltered request - a filter that
        // matches nothing, a failed request, or stale data must not read as an empty fleet.
        actions: [
          { type: 'dispatch-event', eventName: CALENDAR_TOUR_PREPARE_FLEET_EVENT },
          { type: 'wait-until-ready', target: CALENDAR_SETUP_TOUR_TARGETS.schedule },
        ],
      },
    },
    {
      id: 'empty-fleet',
      route: '/calendar',
      target: CALENDAR_SETUP_TOUR_TARGETS.schedule,
      content:
        'No fleet groups are available yet. Add groups, models, and vehicles to show fleet rows and block actions in the Calendar.',
      skipUnlessTargetAttribute: {
        target: CALENDAR_SETUP_TOUR_TARGETS.schedule,
        attribute: 'data-tour-fleet-state',
        value: 'empty',
      },
      skipOnBack: true,
      next: {
        label: 'Done',
        guardTarget: CALENDAR_SETUP_TOUR_TARGETS.emptyFleetReady,
        completesTour: true,
      },
    },
    {
      id: 'groups',
      route: '/calendar',
      target: CALENDAR_SETUP_TOUR_TARGETS.groups,
      content: 'Vehicles are organized into groups like these — for example, small cars or SUVs.',
      next: {
        // Collapsing a group hides its models; re-expanding every group (models
        // collapsed) restores the models target and lets models' Next open vehicles.
        guardTarget: CALENDAR_SETUP_TOUR_TARGETS.schedule,
        action: { type: 'click', target: CALENDAR_SETUP_TOUR_TARGETS.expandGroups },
      },
    },
    {
      id: 'models',
      route: '/calendar',
      target: CALENDAR_SETUP_TOUR_TARGETS.models,
      content:
        'Each group contains vehicle models. Clicking Next opens this one to show its available vehicles.',
      directNextTargets: [CALENDAR_SETUP_TOUR_TARGETS.models],
      next: {
        // The row toggles, so Back from vehicles would otherwise collapse it. Resetting
        // to expanded groups with collapsed models first makes this click always open it.
        actions: [
          { type: 'click', target: CALENDAR_SETUP_TOUR_TARGETS.expandGroups },
          { type: 'click' },
        ],
      },
    },
    {
      id: 'vehicles',
      route: '/calendar',
      target: CALENDAR_SETUP_TOUR_TARGETS.vehicles,
      content:
        'These are the individual vehicles — identified by license plate — available in this model.',
    },
    {
      id: 'add-block',
      route: '/calendar',
      target: CALENDAR_SETUP_TOUR_TARGETS.addBlock,
      content:
        'The + button blocks a vehicle for maintenance, inspection, or other unavailability. Clicking Next opens the form.',
      directNextTargets: [CALENDAR_SETUP_TOUR_TARGETS.addBlock],
      next: {
        action: { type: 'click' },
      },
    },
    {
      id: 'block-modal-fields',
      route: '/calendar',
      target: CALENDAR_SETUP_TOUR_TARGETS.blockModalFields,
      // Spotlight the whole form so the area under an open Start/End Time picker stays
      // clickable. The modal's X and Create/Cancel footer stay under the mask: they close
      // the form, and with it the Done guard, while this step is still showing.
      highlightTarget: CALENDAR_SETUP_TOUR_TARGETS.blockForm,
      content: 'Block Type, Start Time, and End Time are required to create a block.',
      // reactour's default placement would land the popover on top of the modal
      // instead of beside it.
      position: 'right',
      showRequiredFieldsOnEnter: true,
      // Back returns to the + step, so close the form it opened.
      onBack: [{ type: 'dispatch-event', eventName: CALENDAR_TOUR_CLOSE_BLOCK_MODAL_EVENT }],
      next: {
        label: 'Done',
        guardTarget: CALENDAR_SETUP_TOUR_TARGETS.blockModalFields,
        completesTour: true,
      },
    },
  ],
};
